// src/components/nauphilus/NauphilusReportList.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Pagination from './Pagination';
import Swal, { SweetAlertOptions } from 'sweetalert2';
import { fetchExcelReport, fetchInterviews, fetchCustomers } from './api/nauphilusApi';
import { Modal, ProgressBar } from 'react-bootstrap';

interface Customer {
  id: number;
  name: string;
}

const NauphilusReportList: React.FC = () => {
  const intl = useIntl();

  const [data, setData] = useState<any[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [searchValue, setSearchValue] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [createdAtDateInit, setCreatedAtDateInit] = useState('');
  const [createdAtDateEnd, setCreatedAtDateEnd] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    searchValue: '',
    searchCustomer: '',
    createdAtDateInit: '',
    createdAtDateEnd: ''
  });

  const [orderType, setOrderType] = useState('fechaRegistro');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [timezone, setTimezone] = useState('');

  // Modal detalle
  const [selected, setSelected] = useState<any | null>(null);

  const memoizedData = useMemo(() => data, [data]);

  // ===== Helpers =====

  // ===============================
  // 🎨 Misma función de colores que el panel
  // ===============================
  const getProgressInfo = (stars: number, appKey?: string) => {

    console.log('Evaluando getProgressInfo con stars:', stars, 'y appKey:', appKey);
    let valor = stars ?? 0;
    if (valor < 0) valor = 0;
    if (valor > 100) valor = 100;

    // 🎨 Colores hexadecimales
    const rojo = "#FF4C4C";      // Muy baja probabilidad
    const naranja = "#FFA500";   // Poca probabilidad
    const amarillo = "#FFFF00";  // Moderada probabilidad
    const verde = "#00C851";     // Alta probabilidad

    let color = "#999999";
    let text = "Sin resultado";

    if (appKey === "CREDEQUIP" || appKey === "ENTRETAIL") {
      if (valor <= 25) {
        color = rojo;
        text = "Muy baja probabilidad de pago";
      } else if (valor <= 50) {
        color = naranja;
        text = "Poca probabilidad de pago";
      } else if (valor <= 75) {
        color = amarillo;
        text = "Moderada probabilidad de pago";
      } else {
        color = verde;
        text = "Alta probabilidad de pago";
      }
    } else if (appKey === "FRAUDCEL") {
      if (valor >= 76) {
        color = rojo;
        text = "Muy baja probabilidad de pago";
      } else if (valor >= 51) {
        color = naranja;
        text = "Poca probabilidad de pago";
      } else if (valor >= 26) {
        color = amarillo;
        text = "Moderada probabilidad de pago";
      } else {
        color = verde;
        text = "Alta probabilidad de pago";
      }
    }

    return { color, text, stars: valor };
  };

  const renderRow = (key: string, value: any): JSX.Element => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <div className="mb-3" key={key} style={{ width: '100%' }}>
          <h5 className="fw-bold">    {intl.formatMessage({ id: key })}</h5>
          <div className="row">
            {Object.entries(value).map(([k, v]) => (
              <React.Fragment key={k}>{renderRow(k, v)}</React.Fragment>
            ))}
          </div>
        </div>
      );
    }
    if (Array.isArray(value)) {
      return (
        <div className="row mb-2" key={key}>
          {value.map((v, i) => renderRow(`${key}[${i}]`, v))}
        </div>
      );
    }
    return (
      <div className="col-md-4 mb-2" key={key}>
        <label className="form-label">{intl.formatMessage({ id: key })}</label>
        <div>{typeof value === 'boolean' ? (value ? 'Sí' : 'No') : value ?? ''}</div>
      </div>
    );
  };

  // ===== API Calls =====
  const fetchCustomerList = useCallback(async () => {
    try {
      const customersResponse = await fetchCustomers();
      setCustomers(customersResponse.customers || customersResponse);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { rows, totalPages, timezone } = await fetchInterviews(
        currentPage,
        limit,
        appliedFilters.searchValue,
        appliedFilters.searchCustomer,
        appliedFilters.createdAtDateInit,
        appliedFilters.createdAtDateEnd,
        orderType,
        orderDirection
      );
      setData(rows);
      setTotalPages(totalPages);
      setTimezone(timezone);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, appliedFilters, orderType, orderDirection]);

  useEffect(() => { fetchCustomerList(); }, [fetchCustomerList]);
  useEffect(() => { loadData(); }, [loadData]);

  // ===== Filtros =====
  const handleSearch = () => {
    setAppliedFilters({
      searchValue,
      searchCustomer,
      createdAtDateInit,
      createdAtDateEnd
    });
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchValue('');
    setSearchCustomer('');
    setCreatedAtDateInit('');
    setCreatedAtDateEnd('');
    setAppliedFilters({ searchValue: '', searchCustomer: '', createdAtDateInit: '', createdAtDateEnd: '' });
    setCurrentPage(1);
  };

  // ===== Descargar CSV =====
  const handleDownload = async () => {
    try {
      Swal.fire({
        html: `<div class="spinner-container"><i class="fas fa-spinner fa-spin"></i> Generando reporte...</div>`,
        showConfirmButton: false,
        allowOutsideClick: false,
      } as SweetAlertOptions);

      const signedUrl = await fetchExcelReport(
        appliedFilters.searchValue,
        appliedFilters.searchCustomer,
        appliedFilters.createdAtDateInit,
        appliedFilters.createdAtDateEnd,
        orderType,
        orderDirection
      );

      if (signedUrl) {
        const link = document.createElement('a');
        link.href = signedUrl;
        link.download = 'nauphilus_report.csv';
        link.click();
      } else {
        console.error('No se pudo generar la URL firmada del archivo.');
      }

      Swal.close();
    } catch (err) {
      Swal.close();
      console.error('Error al generar el reporte:', err);
    }
  };

  // ===== Render =====
  if (isLoading) return <div>Cargando...</div>;
  if (error) return <div>Error al cargar los datos</div>;

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-body py-3">

        {/* Filtros */}
        <div className="row flex-wrap mb-5">
          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Buscar</label>
            <input
              type="text"
              className="form-control form-control-solid form-control-lg"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Socio comercial</label>
            <select
              className="form-select form-select-solid form-control-lg"
              value={searchCustomer}
              onChange={(e) => setSearchCustomer(e.target.value)}
            >
              <option value="">Selecciona un socio comercial</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Fecha inicio</label>
            <input
              type="date"
              className="form-control form-control-solid form-control-lg"
              value={createdAtDateInit}
              onChange={(e) => setCreatedAtDateInit(e.target.value)}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <label>Fecha fin</label>
            <input
              type="date"
              className="form-control form-control-solid form-control-lg"
              value={createdAtDateEnd}
              onChange={(e) => setCreatedAtDateEnd(e.target.value)}
            />
          </div>
        </div>

        {/* Botones */}
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary me-2" onClick={handleSearch}>Buscar</button>
          <button className="btn btn-secondary me-2" onClick={handleReset}>Borrar</button>
          <button className="btn btn-primary" onClick={handleDownload}>Descargar reporte</button>
        </div>

        {/* Tabla */}
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Socio comercial</th>
                <th>ID Global</th>
                <th>ID Hub</th>
                <th>Nombres</th>
                <th>Apellido Paterno</th>
                <th>Apellido Materno</th>
                <th>Estado</th>
                <th>Fecha registro</th>
                <th>Hora registro</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {memoizedData.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center">Sin resultados</td>
                </tr>
              ) : (
                memoizedData.map((r, idx) => {
                  const isCompleted = r.providerResponse && Object.keys(r.providerResponse).length > 0;

                  return (
                    <tr key={r.id}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>{r.customerName}</td>
                      <td>{r.idGlobal}</td>
                      <td>{r.idHub}</td>
                      <td>{r.nombres}</td>
                      <td>{r.apellidoPaterno}</td>
                      <td>{r.apellidoMaterno}</td>
                      <td>{isCompleted ? 'Completada' : 'Pendiente'}</td>
                      <td>{r.fechaRegistro}</td>
                      <td>{r.horaRegistro}</td>
                      <td>
                        {/* Botón Ver detalle */}
                        <button
                          className="btn btn-primary btn-sm me-1"
                          onClick={isCompleted ? () => setSelected(r) : undefined}
                          disabled={!isCompleted}
                          style={{
                            backgroundColor: 'var(--kt-primary)',
                            borderColor: 'var(--kt-primary)',
                            color: 'white',
                            pointerEvents: isCompleted ? 'auto' : 'none',
                            opacity: 1,
                          }}
                          title={isCompleted ? 'Ver detalle' : 'Detalle no disponible'}
                          aria-label={`Detalle de entrevista ${r.idGlobal}`}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="d-flex justify-content-between mt-3">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="form-select form-select-sm"
            style={{ width: '80px' }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          {memoizedData.length > 0 && (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              limit={limit}
              lengthData={memoizedData.length}
            />
          )}
        </div>

        {/* Modal detalle */}
        {selected && (
          <Modal show={!!selected} onHide={() => setSelected(null)} size="xl" centered>
            <Modal.Header closeButton>
              <Modal.Title>
                Entrevista #{selected.idGlobal} – {selected.appKey || 'N/A'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selected.providerResponse ? (
                <>
                  {/* Barra de progreso */}
                  {(() => {
                    // 🔢 Convertimos las estrellas a número
                    const starsRaw = selected.providerResponse?.stars ?? 0;
                    const stars = Number(starsRaw);

                    const { color, text } = getProgressInfo(stars, selected.appKey);
                    return (
                      <div className="mb-3">
                        <ProgressBar
                          now={stars}
                          label={`${stars.toFixed(0)}%`}
                          style={{
                            backgroundColor: "#e0e0e0",
                            height: "25px",
                            fontWeight: "bold",
                          }}
                        >
                          <div
                            style={{
                              width: `${stars}%`,
                              backgroundColor: color,
                              height: "100%",
                              borderRadius: "0.25rem",
                              transition: "width 0.6s ease",
                              textAlign: "center",
                              color: "#000",
                              fontWeight: "bold",
                            }}
                          >
                            {`${stars.toFixed(0)}%`}
                          </div>
                        </ProgressBar>
                        <div className="mt-2 text-center fw-semibold">{text}</div>
                      </div>
                    );
                  })()}


                  {/* Detalle jerárquico */}
                  <div className="container-fluid" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {renderRow('root', selected.providerResponse)}
                  </div>
                </>
              ) : (
                <p>No hay datos para mostrar</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Cerrar</button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default NauphilusReportList;
